import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import SEO from "../utils/SEO";
import SubscriptionForm from "../utils/SubscriptionForm";
import { getQuiz } from "../../data/quizzes";
import { getQuizIcon } from "./quizIcons";
import QuizEvidence from "./QuizEvidence";
import { scoreQuiz } from "../../utils/scoreQuiz";
import { trackQuizStart, trackQuizAnswer, trackQuizComplete } from "../../utils/analytics";
import "../../styles/Quiz.css";

// Generic, data-driven quiz engine. Renders any quiz in the registry
// (client/src/data/quizzes) one question at a time, gates the result behind the
// email-capture SubscriptionForm (writes to quiz_leads, segmented by path), then
// routes to the shareable result page. The Dosha quiz keeps its own component.
const Quiz = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const def = getQuiz(slug);

  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (def) {
      setCurrent(0);
      setAnswers([]);
      trackQuizStart();
    }
  }, [def, slug]);

  if (!def) {
    return (
      <div className="quiz-page" style={{ textAlign: "center" }}>
        <SEO title="Quiz not found | Sarv" noIndex={true} />
        <h1 className="quiz-hub__title">Quiz not found</h1>
        <p className="quiz-intro">
          Browse all our <Link to="/quizzes">wellness quizzes →</Link>
        </p>
      </div>
    );
  }

  const total = def.questions.length;
  const q = def.questions[current];
  const selected = answers[current];
  const isLast = current === total - 1;
  const Icon = getQuizIcon(def.slug);

  const selectOption = (optIndex) => {
    const next = [...answers];
    next[current] = optIndex;
    setAnswers(next);
    trackQuizAnswer(current, q.options[optIndex]?.label);
  };

  const goNext = () => {
    if (selected == null) return;
    if (isLast) setShowForm(true);
    else setCurrent((c) => c + 1);
  };

  const goBack = () => setCurrent((c) => Math.max(0, c - 1));

  const onFormSubmit = () => {
    setShowForm(false);
    const { key } = scoreQuiz(def, answers);
    trackQuizComplete(`${def.slug}:${key}`);
    navigate(`/quiz/${def.slug}/result/${key}`);
  };

  const progress = Math.round(((current + (selected != null ? 1 : 0)) / total) * 100);

  return (
    <div className="quiz-page">
      <SEO
        title={def.seoTitle}
        description={def.seoDescription}
        canonicalPath={`/quiz/${def.slug}`}
        keywords={def.keywords}
      />

      <div className="quiz-progress">
        <div className="quiz-progress__track">
          <div className="quiz-progress__fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="quiz-progress__count">
          {current + 1} / {total}
        </span>
      </div>

      {current === 0 && (
        <>
          <p className="quiz-eyebrow">
            <span className="quiz-eyebrow__icon" aria-hidden="true">
              <Icon />
            </span>
            {def.title}
          </p>
          <p className="quiz-intro">{def.intro}</p>
          <p className="quiz-meta">
            {total} questions · about {Math.max(2, Math.round(total * 0.2))} min · evidence-based
          </p>
        </>
      )}

      <div className="quiz-card">
        {q.section && <p className="quiz-section">{q.section}</p>}
        <h1 className="quiz-question">{q.question}</h1>
        <div className="quiz-options" role="radiogroup" aria-label={q.question}>
          {q.options.map((opt, i) => {
            const isSel = selected === i;
            return (
              <button
                type="button"
                key={i}
                role="radio"
                aria-checked={isSel}
                className={`quiz-option${isSel ? " quiz-option--selected" : ""}`}
                onClick={() => selectOption(i)}
              >
                <span className="quiz-option__bullet" />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>

        <div className="quiz-nav">
          <button
            type="button"
            className="quiz-btn quiz-btn--ghost"
            onClick={goBack}
            disabled={current === 0}
          >
            Back
          </button>
          <button
            type="button"
            className="quiz-btn quiz-btn--primary"
            onClick={goNext}
            disabled={selected == null}
          >
            {isLast ? "See my results" : "Next"}
          </button>
        </div>
      </div>

      {current === 0 && (
        <QuizEvidence note={def.evidenceNote} references={def.references} />
      )}

      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        centered
        contentClassName="quiz-modal"
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <SubscriptionForm onFormSubmit={onFormSubmit} />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Quiz;
